import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { 
  TrendingUp, TrendingDown, Activity, Target, Award, 
  BarChart3, PieChart, Zap, Trophy, Skull, Filter,
  ChevronUp, ChevronDown, RefreshCw, Calendar, X
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { QuickStatsWidget } from "@/components/QuickStatsWidget";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = {
  primary: '#06b6d4',
  secondary: '#3b82f6',
  profit: '#10b981',
  loss: '#f43f5e',
  warning: '#f59e0b',
  muted: '#71717a'
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-950/95 border border-white/10 backdrop-blur-xl rounded-lg shadow-xl p-3 text-xs font-mono">
        <p className="text-white/60 mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey || entry.name} style={{ color: entry.color }} className="font-semibold">
            {entry.name}: ₹{entry.value?.toLocaleString('en-IN')}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Metric Card Component
const MetricCard = ({ label, value, subValue, icon: Icon, trend, size = "normal", onClick, testId, isCurrency = true }) => {
  const isProfit = typeof value === 'number' && value > 0;
  const isLoss = typeof value === 'number' && value < 0;
  
  // Format value - always show full numbers
  const formatValue = (val) => {
    if (typeof val !== 'number') return val;
    
    if (!isCurrency) {
      // For non-currency values (like counts), just show the number
      return val.toLocaleString('en-IN');
    }
    
    // For currency values - always show full number with Indian formatting
    return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  return (
    <div 
      data-testid={testId}
      onClick={onClick}
      className={`
        rounded-xl border border-white/5 bg-zinc-900/40 backdrop-blur-md 
        shadow-2xl shadow-black/40 overflow-hidden relative group 
        transition-all duration-300 hover:border-white/10 hover:bg-zinc-900/60
        ${onClick ? 'cursor-pointer' : ''}
        ${size === 'large' ? 'p-6' : 'p-5'}
      `}
    >
      {/* Glow effect for profit/loss */}
      {isProfit && <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/5 to-transparent pointer-events-none" />}
      {isLoss && <div className="absolute inset-0 bg-gradient-to-t from-rose-500/5 to-transparent pointer-events-none" />}
      
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
          {Icon && <Icon className={`w-4 h-4 ${isProfit ? 'text-emerald-400' : isLoss ? 'text-rose-400' : 'text-zinc-500'}`} />}
        </div>
        <div className={`font-mono ${size === 'large' ? 'text-4xl' : 'text-2xl'} font-bold tracking-tight ${
          isProfit ? 'text-emerald-400' : isLoss ? 'text-rose-400' : 'text-white'
        }`}>
          {formatValue(value)}
        </div>
        {subValue && <div className="text-xs text-zinc-500 mt-1 font-mono">{subValue}</div>}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {trend >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Chart Card Component
const ChartCard = ({ title, children, className = "", testId }) => (
  <div 
    data-testid={testId}
    className={`rounded-xl border border-white/5 bg-zinc-900/40 backdrop-blur-md shadow-2xl shadow-black/40 overflow-hidden ${className}`}
  >
    <div className="flex items-center justify-between p-5 border-b border-white/5">
      <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-400">{title}</h3>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

// Win Rate Gauge
const WinRateGauge = ({ winRate, wins, losses }) => {
  const data = [
    { name: 'Wins', value: wins, color: COLORS.profit },
    { name: 'Losses', value: losses, color: COLORS.loss }
  ];
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPie>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </RechartsPie>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="font-mono text-3xl font-bold text-white">{winRate}%</div>
            <div className="text-xs text-zinc-500">Win Rate</div>
          </div>
        </div>
      </div>
      <div className="flex gap-6 mt-4">
        <div className="text-center">
          <div className="font-mono text-xl font-bold text-emerald-400">{wins}</div>
          <div className="text-xs text-zinc-500">Wins</div>
        </div>
        <div className="text-center">
          <div className="font-mono text-xl font-bold text-rose-400">{losses}</div>
          <div className="text-xs text-zinc-500">Losses</div>
        </div>
      </div>
    </div>
  );
};

// Hexagonal Stat Component
const HexStat = ({ label, value, color = "cyan" }) => {
  const colorClasses = {
    cyan: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 text-cyan-400',
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400',
    rose: 'from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-400',
    blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400'
  };
  
  return (
    <div className={`bg-gradient-to-b ${colorClasses[color]} border rounded-xl p-4 text-center`}>
      <div className="font-mono text-2xl font-bold">{value}</div>
      <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  
  // Filters
  const [filters, setFilters] = useState({
    year: "",
    month: "",
    strategy_id: "",
    account_id: "",
    direction: "",
    symbol: "",
    from_date: "",
    to_date: "",
    period_type: "" // "cy_YYYY", "fy_YYYY-YYYY", or "custom"
  });

  // Generate year options
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  
  // Calendar Years (last 5 years)
  const calendarYears = [];
  for (let y = currentYear; y >= currentYear - 4; y--) {
    calendarYears.push({ value: `cy_${y}`, label: `CY ${y}`, year: y });
  }
  
  // Fiscal Years (April to March) - last 5 fiscal years
  // FY 2025-2026 means April 2025 to March 2026
  const fiscalYears = [];
  // Determine current fiscal year
  const currentFYStart = currentMonth >= 4 ? currentYear : currentYear - 1;
  for (let y = currentFYStart; y >= currentFYStart - 4; y--) {
    fiscalYears.push({ 
      value: `fy_${y}-${y + 1}`, 
      label: `FY ${y}-${y + 1}`,
      fromDate: `${y}-04-01`,
      toDate: `${y + 1}-03-31`
    });
  }

  // Helper to get date range from period selection
  const getDateRangeFromPeriod = (periodValue) => {
    if (!periodValue || periodValue === "all") {
      return { from_date: "", to_date: "" };
    }
    
    if (periodValue.startsWith("cy_")) {
      // Calendar Year: Jan 1 to Dec 31
      const year = periodValue.replace("cy_", "");
      return { from_date: `${year}-01-01`, to_date: `${year}-12-31` };
    }
    
    if (periodValue.startsWith("fy_")) {
      // Fiscal Year: April 1 to March 31
      const fy = fiscalYears.find(f => f.value === periodValue);
      if (fy) {
        return { from_date: fy.fromDate, to_date: fy.toDate };
      }
    }
    
    if (periodValue === "custom") {
      // Custom will be handled by the date picker
      return { from_date: filters.from_date, to_date: filters.to_date };
    }
    
    return { from_date: "", to_date: "" };
  };

  const handlePeriodChange = (value) => {
    if (value === "custom") {
      setShowCustomDatePicker(true);
      setFilters(prev => ({ ...prev, period_type: "custom", year: "" }));
    } else {
      setShowCustomDatePicker(false);
      const dateRange = getDateRangeFromPeriod(value);
      setFilters(prev => ({ 
        ...prev, 
        period_type: value,
        year: "", // Clear legacy year filter
        ...dateRange
      }));
    }
  };

  const applyCustomDateRange = () => {
    if (customDateRange.from && customDateRange.to) {
      setFilters(prev => ({
        ...prev,
        period_type: "custom",
        from_date: format(customDateRange.from, "yyyy-MM-dd"),
        to_date: format(customDateRange.to, "yyyy-MM-dd")
      }));
      setShowCustomDatePicker(false);
    } else {
      toast.error("Please select both From and To dates");
    }
  };

  // Get display label for current period filter
  const getPeriodDisplayLabel = () => {
    if (!filters.period_type || filters.period_type === "all") return "All Periods";
    if (filters.period_type === "custom" && filters.from_date && filters.to_date) {
      return `${filters.from_date} to ${filters.to_date}`;
    }
    if (filters.period_type.startsWith("cy_")) {
      return `CY ${filters.period_type.replace("cy_", "")}`;
    }
    if (filters.period_type.startsWith("fy_")) {
      return `FY ${filters.period_type.replace("fy_", "")}`;
    }
    return "All Periods";
  };

  useEffect(() => {
    fetchData();
    fetchFiltersData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchFiltersData = async () => {
    try {
      const [strategiesRes, accountsRes, tradesRes] = await Promise.all([
        api.get("/strategies"),
        api.get("/accounts"),
        api.get("/trades")
      ]);
      setStrategies(strategiesRes.data);
      setAccounts(accountsRes.data);
      
      // Extract unique symbols from trades, sorted alphabetically
      const uniqueSymbols = [...new Set(tradesRes.data.map(t => t.symbol))].filter(Boolean).sort();
      setSymbols(uniqueSymbols);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      // Use date range if available, otherwise fall back to year
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (!filters.from_date && !filters.to_date && filters.year) {
        params.append('year', filters.year);
      }
      if (filters.month) params.append('month', filters.month);
      if (filters.strategy_id) params.append('strategy_id', filters.strategy_id);
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.symbol) params.append('symbol', filters.symbol);
      
      const response = await api.get(`/analytics/dashboard?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Don't show error toast - the UI will show "no trades" message
      setData({ summary: { total_trades: 0 }, charts: {}, trades: {}, filters: {} });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      year: "",
      month: "",
      strategy_id: "",
      account_id: "",
      direction: "",
      symbol: "",
      from_date: "",
      to_date: "",
      period_type: ""
    });
    setCustomDateRange({ from: null, to: null });
    setShowCustomDatePicker(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <div className="text-zinc-400 font-mono text-sm">Loading analytics...</div>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const charts = data?.charts || {};

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-sans font-bold tracking-tight text-3xl md:text-4xl text-white mb-1">
            Performance Dashboard
          </h1>
          <p className="text-zinc-500 text-sm font-mono">Track your trading analytics and performance metrics</p>
        </div>
        <Button 
          onClick={handleRefresh} 
          disabled={refreshing}
          className="bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 p-4 rounded-xl bg-zinc-900/40 border border-white/5" data-testid="filters-section">
        <div className="flex items-center gap-2 text-zinc-500">
          <Filter className="w-4 h-4" />
          <span className="text-xs font-mono uppercase">Filters</span>
        </div>
        
        <Select value={filters.account_id || "all"} onValueChange={(v) => setFilters({...filters, account_id: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[140px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="Account" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map(a => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Period Filter - CY, FY, Custom */}
        <Select 
          value={filters.period_type || "all"} 
          onValueChange={handlePeriodChange}
        >
          <SelectTrigger className="w-[160px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="Period">
              {getPeriodDisplayLabel()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 max-h-80">
            <SelectItem value="all">All Periods</SelectItem>
            
            <SelectSeparator className="bg-white/10 my-1" />
            <SelectGroup>
              <SelectLabel className="text-zinc-500 text-[10px] uppercase">Calendar Year</SelectLabel>
              {calendarYears.map(cy => (
                <SelectItem key={cy.value} value={cy.value}>{cy.label}</SelectItem>
              ))}
            </SelectGroup>
            
            <SelectSeparator className="bg-white/10 my-1" />
            <SelectGroup>
              <SelectLabel className="text-zinc-500 text-[10px] uppercase">Fiscal Year (Apr-Mar)</SelectLabel>
              {fiscalYears.map(fy => (
                <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>
              ))}
            </SelectGroup>
            
            <SelectSeparator className="bg-white/10 my-1" />
            <SelectItem value="custom">
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                Custom Range...
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Custom Date Range Dialog */}
        <Dialog open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                Select Custom Date Range
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="grid grid-cols-2 gap-6">
                {/* From Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-300">From Date</label>
                  <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                    <CalendarComponent
                      mode="single"
                      selected={customDateRange.from}
                      onSelect={(date) => setCustomDateRange(prev => ({ ...prev, from: date }))}
                      className="rounded-md"
                    />
                  </div>
                  {customDateRange.from && (
                    <div className="text-xs text-cyan-400 font-mono">
                      Selected: {format(customDateRange.from, "dd MMM yyyy")}
                    </div>
                  )}
                </div>
                
                {/* To Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-300">To Date</label>
                  <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                    <CalendarComponent
                      mode="single"
                      selected={customDateRange.to}
                      onSelect={(date) => setCustomDateRange(prev => ({ ...prev, to: date }))}
                      className="rounded-md"
                    />
                  </div>
                  {customDateRange.to && (
                    <div className="text-xs text-cyan-400 font-mono">
                      Selected: {format(customDateRange.to, "dd MMM yyyy")}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Selected Range Summary */}
              {customDateRange.from && customDateRange.to && (
                <div className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <div className="text-sm text-cyan-400">
                    <span className="font-medium">Selected Range:</span>{" "}
                    {format(customDateRange.from, "dd MMM yyyy")} → {format(customDateRange.to, "dd MMM yyyy")}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setCustomDateRange({ from: null, to: null });
                }}
                className="border-white/10 text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button 
                onClick={applyCustomDateRange}
                disabled={!customDateRange.from || !customDateRange.to}
                className="bg-cyan-500 hover:bg-cyan-400 text-black disabled:opacity-50"
              >
                Apply Range
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Select value={filters.strategy_id || "all"} onValueChange={(v) => setFilters({...filters, strategy_id: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[140px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="Strategy" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Strategies</SelectItem>
            {strategies.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.direction || "all"} onValueChange={(v) => setFilters({...filters, direction: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[100px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Directions</SelectItem>
            <SelectItem value="LONG">Long</SelectItem>
            <SelectItem value="SHORT">Short</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.symbol || "all"} onValueChange={(v) => setFilters({...filters, symbol: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[140px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="Symbol" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
            <SelectItem value="all">All Symbols</SelectItem>
            {symbols.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filters.period_type || filters.strategy_id || filters.direction || filters.symbol || filters.account_id) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-500 hover:text-white text-xs">
            Clear
          </Button>
        )}
      </div>

      {/* Quick Stats Widget - Today's Performance */}
      <QuickStatsWidget accountId={filters.account_id} />

      {/* No Trades Message */}
      {summary.total_trades === 0 && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Filter className="w-8 h-8 text-amber-400" />
          </div>
          <h3 className="text-lg font-medium text-amber-400 mb-2">No Trades Found</h3>
          <p className="text-sm text-zinc-400 max-w-md mx-auto">
            {filters.period_type || filters.strategy_id || filters.direction || filters.symbol || filters.account_id 
              ? "No trades match your selected filters. Try adjusting the filters or select a different period."
              : "You don't have any trades yet. Start adding trades to see your analytics here."
            }
          </p>
          {(filters.period_type || filters.strategy_id || filters.direction || filters.symbol || filters.account_id) && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters} 
              className="mt-4 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            >
              Clear All Filters
            </Button>
          )}
        </div>
      )}

      {/* Top Metrics Row - Only show if trades exist */}
      {summary.total_trades > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard 
          label="Net P&L" 
          value={summary.net_pnl} 
          icon={Activity}
          size="large"
          testId="net-pnl-card"
        />
        <MetricCard 
          label="Gross Profit" 
          value={summary.gross_profit} 
          icon={TrendingUp}
          testId="gross-profit-card"
        />
        <MetricCard 
          label="Gross Loss" 
          value={-summary.gross_loss} 
          icon={TrendingDown}
          testId="gross-loss-card"
        />
        <MetricCard 
          label="Total Trades" 
          value={summary.total_trades}
          subValue={`${summary.open_trades} open · ${summary.closed_trades} closed`}
          icon={Target}
          onClick={() => navigate("/trades")}
          testId="total-trades-card"
          isCurrency={false}
        />
      </div>
      )}

      {/* Main Grid - Only show if trades exist */}
      {summary.total_trades > 0 && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Equity Curve - Large */}
        <ChartCard title="Equity Curve" className="lg:col-span-8" testId="equity-curve-chart">
          <div className="h-[300px]">
            {charts.equity_curve?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.equity_curve}>
                  <defs>
                    <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#52525b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#52525b" fontSize={10} tickLine={false} tickFormatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="pnl" 
                    stroke={COLORS.primary} 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorPnl)" 
                    name="Net P&L"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-500 font-mono text-sm">
                No trade data available
              </div>
            )}
          </div>
        </ChartCard>

        {/* Win Rate & Stats - Right Side */}
        <div className="lg:col-span-4 space-y-4">
          <ChartCard title="Win Rate" testId="win-rate-chart">
            <WinRateGauge 
              winRate={summary.win_rate || 0} 
              wins={summary.win_count || 0} 
              losses={summary.loss_count || 0} 
            />
          </ChartCard>
          
          {/* Key Ratios */}
          <div className="grid grid-cols-2 gap-3">
            <HexStat label="Profit Factor" value={summary.profit_factor || 0} color="cyan" />
            <HexStat label="Risk/Reward" value={summary.risk_reward?.toFixed(1) || 0} color="blue" />
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* P&L Last 30 Days */}
        <ChartCard title="P&L Last 30 Days" className="lg:col-span-2" testId="pnl-30-days-chart">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.pnl_last_30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#52525b" fontSize={9} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#52525b" fontSize={10} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="pnl" name="P&L">
                  {charts.pnl_last_30?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? COLORS.profit : COLORS.loss} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Wins vs Losses Stats */}
        <ChartCard title="Wins vs Losses" testId="wins-losses-card">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <div className="font-mono text-2xl font-bold text-emerald-400">{summary.win_count || 0}</div>
                <div className="text-xs text-zinc-500">Winning Trades</div>
              </div>
              <div className="ml-auto text-right">
                <div className="font-mono text-lg text-emerald-400">₹{(summary.avg_win || 0).toLocaleString('en-IN')}</div>
                <div className="text-xs text-zinc-500">Avg Win</div>
              </div>
            </div>
            
            <div className="h-px bg-white/5" />
            
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center">
                <Skull className="w-6 h-6 text-rose-400" />
              </div>
              <div>
                <div className="font-mono text-2xl font-bold text-rose-400">{summary.loss_count || 0}</div>
                <div className="text-xs text-zinc-500">Losing Trades</div>
              </div>
              <div className="ml-auto text-right">
                <div className="font-mono text-lg text-rose-400">₹{(summary.avg_loss || 0).toLocaleString('en-IN')}</div>
                <div className="text-xs text-zinc-500">Avg Loss</div>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Third Row - Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* P&L by Day of Week */}
        <ChartCard title="P&L by Day" className="lg:col-span-2" testId="pnl-by-day-chart">
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.pnl_by_day} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis type="number" stroke="#52525b" fontSize={10} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                <YAxis type="category" dataKey="day" stroke="#52525b" fontSize={10} tickLine={false} width={60} tickFormatter={(d) => d.slice(0, 3)} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="pnl" name="P&L" radius={[0, 4, 4, 0]}>
                  {charts.pnl_by_day?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? COLORS.profit : COLORS.loss} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Streaks */}
        <ChartCard title="Trading Streaks" testId="streaks-card">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Max Win Streak</span>
              <span className="font-mono text-lg text-emerald-400">{summary.max_win_streak || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Max Loss Streak</span>
              <span className="font-mono text-lg text-rose-400">{summary.max_loss_streak || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Current Streak</span>
              <span className={`font-mono text-lg ${summary.current_streak >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {summary.current_streak || 0}
              </span>
            </div>
          </div>
        </ChartCard>

        {/* Top/Bottom Trades */}
        <ChartCard title="Best & Worst" testId="top-bottom-card">
          <div className="space-y-3">
            {data?.trades?.top_trades?.slice(0, 2).map((trade, i) => (
              <div key={`top-${i}`} className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 truncate max-w-[80px]">{trade.symbol}</span>
                <span className="font-mono text-emerald-400">+₹{trade.pnl?.toLocaleString('en-IN')}</span>
              </div>
            ))}
            <div className="h-px bg-white/5" />
            {data?.trades?.bottom_trades?.slice(0, 2).map((trade, i) => (
              <div key={`bottom-${i}`} className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 truncate max-w-[80px]">{trade.symbol}</span>
                <span className="font-mono text-rose-400">₹{trade.pnl?.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
